/**
 * Data for "3.4 Million PRs, One Uncomfortable Truth" research report
 * Source: GitHub Archive / BigQuery - December 2024
 */

export const studyMeta = {
  totalPRs: 3_387_250,
  timeRange: 'December 2024',
  dataSource: 'GitHub Archive / BigQuery',
  title: '3.4 Million PRs, One Uncomfortable Truth',
  subtitle: "What GitHub's largest code review study reveals about how software really ships",
};

// Query A + E: Review rates by PR size
export const reviewBySize = [
  {
    bucket: 'Tiny (<10 lines)',
    shortLabel: '<10',
    prCount: 998205,
    pctNoReview: 70.6,
    avgReviewComments: 0.03,
    commentsPerLine: 0.91,
    avgApprovals: 0.37,
  },
  {
    bucket: 'Small (10-50)',
    shortLabel: '10-50',
    prCount: 763798,
    pctNoReview: 72.6,
    avgReviewComments: 0.15,
    commentsPerLine: 0.64,
    avgApprovals: 0.32,
  },
  {
    bucket: 'Medium (50-200)',
    shortLabel: '50-200',
    prCount: 670849,
    pctNoReview: 74.7,
    avgReviewComments: 0.41,
    commentsPerLine: 0.41,
    avgApprovals: 0.3,
  },
  {
    bucket: 'Large (200-500)',
    shortLabel: '200-500',
    prCount: 377381,
    pctNoReview: 76.5,
    avgReviewComments: 0.76,
    commentsPerLine: 0.25,
    avgApprovals: 0.28,
  },
  {
    bucket: 'XL (500-1000)',
    shortLabel: '500-1K',
    prCount: 204532,
    pctNoReview: 78.0,
    avgReviewComments: 1.1,
    commentsPerLine: 0.16,
    avgApprovals: 0.27,
  },
  {
    bucket: 'Massive (1000+)',
    shortLabel: '1000+',
    prCount: 372485,
    pctNoReview: 82.6,
    avgReviewComments: 1.09,
    commentsPerLine: 0.05,
    avgApprovals: 0.21,
  },
];

// Query D: Self-merge statistics
export const selfMergeData = {
  selfMerged: 2346381,
  mergedByOther: 1102410,
  selfMergePercent: 68.03,
  otherMergePercent: 31.97,
};

// Query B: First-time vs repeat contributor data
export const contributorData = {
  firstTime: {
    label: 'First-time contributor',
    prCount: 133315,
    avgHours: 95.8,
    medianHours: 26.0,
    p90Hours: 305.0,
  },
  repeat: {
    label: 'Repeat contributor',
    prCount: 913530,
    avgHours: 68.1,
    medianHours: 17.0,
    p90Hours: 199.0,
  },
  waitPenaltyPercent: 53, // (26-17)/17 * 100
  p90PenaltyPercent: 53, // (305-199)/199 * 100
};

// Query G: Day of week merge distribution
export const mergeDayData = [
  { dayOfWeek: 1, dayName: 'Sunday', merges: 388062, percentage: 11.25 },
  { dayOfWeek: 2, dayName: 'Monday', merges: 658296, percentage: 19.08 },
  { dayOfWeek: 3, dayName: 'Tuesday', merges: 622722, percentage: 18.05 },
  { dayOfWeek: 4, dayName: 'Wednesday', merges: 500485, percentage: 14.51 },
  { dayOfWeek: 5, dayName: 'Thursday', merges: 480401, percentage: 13.93 },
  { dayOfWeek: 6, dayName: 'Friday', merges: 481768, percentage: 13.97 },
  { dayOfWeek: 7, dayName: 'Saturday', merges: 317648, percentage: 9.21 },
];

// Query F: Time of day merge distribution
export const mergeHourData = [
  { hour: 0, mergeCount: 120399, percentage: 3.49 },
  { hour: 1, mergeCount: 115943, percentage: 3.36 },
  { hour: 2, mergeCount: 115762, percentage: 3.36 },
  { hour: 3, mergeCount: 117304, percentage: 3.4 },
  { hour: 4, mergeCount: 113530, percentage: 3.29 },
  { hour: 5, mergeCount: 111845, percentage: 3.24 },
  { hour: 6, mergeCount: 126287, percentage: 3.66 },
  { hour: 7, mergeCount: 139792, percentage: 4.05 },
  { hour: 8, mergeCount: 152054, percentage: 4.41 },
  { hour: 9, mergeCount: 155553, percentage: 4.51 },
  { hour: 10, mergeCount: 159402, percentage: 4.62 },
  { hour: 11, mergeCount: 156349, percentage: 4.53 },
  { hour: 12, mergeCount: 160592, percentage: 4.66 },
  { hour: 13, mergeCount: 163838, percentage: 4.75 },
  { hour: 14, mergeCount: 177223, percentage: 5.14 },
  { hour: 15, mergeCount: 179144, percentage: 5.19 },
  { hour: 16, mergeCount: 184927, percentage: 5.36 },
  { hour: 17, mergeCount: 169692, percentage: 4.92 },
  { hour: 18, mergeCount: 157892, percentage: 4.58 },
  { hour: 19, mergeCount: 150954, percentage: 4.38 },
  { hour: 20, mergeCount: 147471, percentage: 4.28 },
  { hour: 21, mergeCount: 141301, percentage: 4.1 },
  { hour: 22, mergeCount: 132090, percentage: 3.83 },
  { hour: 23, mergeCount: 100038, percentage: 2.9 },
];

// Query H: Time to merge buckets
export const timeToMergeData = [
  { bucket: 'Same day (<24h)', prCount: 598631, percentage: 52.67 },
  { bucket: '1-3 days', prCount: 182712, percentage: 16.08 },
  { bucket: '3-7 days', prCount: 139240, percentage: 12.25 },
  { bucket: '1-2 weeks', prCount: 82874, percentage: 7.29 },
  { bucket: '2-4 weeks', prCount: 58609, percentage: 5.16 },
  { bucket: 'Over 1 month', prCount: 74449, percentage: 6.55 },
];

// Query C: Language statistics (top 15)
export const languageData = [
  { language: 'TypeScript', mergedPRs: 204152, avgHours: 65.6, medianHours: 16.0, avgPRSize: 377 },
  { language: 'Python', mergedPRs: 129702, avgHours: 78.3, medianHours: 21.0, avgPRSize: 271 },
  { language: 'JavaScript', mergedPRs: 89678, avgHours: 67.1, medianHours: 16.0, avgPRSize: 353 },
  { language: 'Java', mergedPRs: 77086, avgHours: 75.0, medianHours: 20.0, avgPRSize: 282 },
  { language: 'Go', mergedPRs: 72837, avgHours: 75.9, medianHours: 20.0, avgPRSize: 260 },
  { language: 'Rust', mergedPRs: 45942, avgHours: 69.7, medianHours: 18.0, avgPRSize: 299 },
  { language: 'C++', mergedPRs: 39135, avgHours: 78.6, medianHours: 22.0, avgPRSize: 320 },
  { language: 'C#', mergedPRs: 36364, avgHours: 63.0, medianHours: 14.0, avgPRSize: 377 },
  { language: 'Shell', mergedPRs: 29077, avgHours: 64.8, medianHours: 15.0, avgPRSize: 111 },
  { language: 'Kotlin', mergedPRs: 24435, avgHours: 76.5, medianHours: 21.0, avgPRSize: 230 },
  { language: 'PHP', mergedPRs: 22271, avgHours: 83.5, medianHours: 22.0, avgPRSize: 327 },
  { language: 'Ruby', mergedPRs: 22001, avgHours: 66.8, medianHours: 16.0, avgPRSize: 145 },
  { language: 'C', mergedPRs: 17615, avgHours: 84.4, medianHours: 24.0, avgPRSize: 345 },
  { language: 'PowerShell', mergedPRs: 9026, avgHours: 30.4, medianHours: 6.0, avgPRSize: 307 },
  { language: 'Dockerfile', mergedPRs: 5704, avgHours: 61.3, medianHours: 11.0, avgPRSize: 85 },
  { language: 'Swift', mergedPRs: 6094, avgHours: 77.2, medianHours: 23.0, avgPRSize: 399 },
];

// Hero statistics for the report
export const heroStats = [
  {
    value: '83%',
    label: 'No Review',
    sublabel: '(1000+ line PRs)',
    description: '83% of massive PRs ship without formal code review',
  },
  {
    value: '68%',
    label: 'Self-Merged',
    sublabel: '(all PRs)',
    description: 'Two-thirds of code merges without a second pair of eyes',
  },
  {
    value: '18x',
    label: 'Less Scrutiny',
    sublabel: '(large PRs)',
    description: 'Review comments per 100 lines drops from 0.91 to 0.05',
  },
  {
    value: '53%',
    label: 'Longer Wait',
    sublabel: '(new contributors)',
    description: 'First-time contributors wait 53% longer for review',
  },
];

// Bot activity analysis
export const botActivityData = {
  december2024: {
    botPRs: 1822367,
    humanPRs: 2989928,
    botPercent: 37.9,
    humanPercent: 62.1,
  },
  yearOverYear: [
    { year: 2020, botPercent: 54.4, totalPRs: 47047784 },
    { year: 2021, botPercent: 57.1, totalPRs: 58100926 },
    { year: 2022, botPercent: 62.0, totalPRs: 76023328 },
    { year: 2023, botPercent: 40.4, totalPRs: 56258783 },
    { year: 2024, botPercent: 34.3, totalPRs: 57239840 },
  ],
  peakYear: 2022,
  peakPercent: 62.0,
  currentPercent: 34.3,
};

// Weekend and work pattern analysis
export const workPatternData = {
  weekendPushPercent: 27.6,
  weekdayPushPercent: 72.4,
  afterHoursPRPercent: 64.0, // PRs opened outside 9-17 UTC
  pushByDay: [
    { day: 'Sunday', percent: 15.47 },
    { day: 'Monday', percent: 16.39 },
    { day: 'Tuesday', percent: 16.67 },
    { day: 'Wednesday', percent: 13.25 },
    { day: 'Thursday', percent: 12.84 },
    { day: 'Friday', percent: 13.20 },
    { day: 'Saturday', percent: 12.18 },
  ],
  peakHours: [
    { hour: 16, percent: 4.95 },
    { hour: 15, percent: 4.79 },
    { hour: 17, percent: 4.69 },
    { hour: 14, percent: 4.65 },
    { hour: 18, percent: 4.55 },
  ],
};

// Project benchmarks - time to first review
export const projectReviewTimeData = {
  fastest: [
    { repo: 'Homebrew/homebrew-core', medianHours: 2.0 },
    { repo: 'Homebrew/homebrew-cask', medianHours: 2.0 },
    { repo: 'home-assistant/core', medianHours: 7.0 },
    { repo: 'rust-lang/rust', medianHours: 8.0 },
    { repo: 'llvm/llvm-project', medianHours: 10.0 },
    { repo: 'vercel/next.js', medianHours: 11.0 },
    { repo: 'NixOS/nixpkgs', medianHours: 13.0 },
  ],
  slowest: [
    { repo: 'Expensify/App', medianHours: 25.0 },
    { repo: 'odoo/odoo', medianHours: 24.0 },
    { repo: 'dagster-io/dagster', medianHours: 24.0 },
    { repo: 'elastic/kibana', medianHours: 18.0 },
    { repo: 'pytorch/pytorch', medianHours: 16.0 },
  ],
  varianceFactor: 12, // 12x variance (2h to 25h)
};

// Project benchmarks - cycle time (open to merge)
export const projectCycleTimeData = {
  fastest: [
    { repo: 'Homebrew/homebrew-cask', medianHours: 0, prCount: 8060 },
    { repo: 'microsoft/vscode', medianHours: 0, prCount: 1651 },
    { repo: 'Homebrew/homebrew-core', medianHours: 1, prCount: 8459 },
    { repo: 'elastic/elasticsearch', medianHours: 2, prCount: 3030 },
    { repo: 'elastic/kibana', medianHours: 3, prCount: 5000 },
    { repo: 'grafana/grafana', medianHours: 3, prCount: 2019 },
    { repo: 'home-assistant/core', medianHours: 4, prCount: 2843 },
  ],
  slowest: [
    { repo: 'zephyrproject-rtos/zephyr', medianHours: 102, prCount: 1813 },
    { repo: 'llvm/llvm-project', medianHours: 23, prCount: 5044 },
    { repo: 'rust-lang/rust', medianHours: 22, prCount: 1570 },
    { repo: 'NixOS/nixpkgs', medianHours: 20, prCount: 13990 },
    { repo: 'cockroachdb/cockroach', medianHours: 19, prCount: 2430 },
  ],
  totalNotableRepos: 84320,
};

// Key quotables for press/social
export const quotables = [
  '83% of pull requests over 1,000 lines ship to production with zero formal code review.',
  'The bigger the change, the less anyone looks at it. Large PRs receive 18x fewer review comments per line of code.',
  "Two-thirds of all code on GitHub is merged by the same person who wrote it.",
  "First-time contributors wait 53% longer for their code to be reviewed. The onboarding tax is real.",
  "Monday isn't just the start of the work week - it's when 1 in 5 PRs finally get merged.",
  "Bot PRs peaked at 62% in 2022, then declined to 34% in 2024.",
  "27% of code pushes happen on weekends. The always-on culture is real.",
  "Review time varies 12x across elite projects - from 2 hours to 25 hours.",
];
